import React, { useState, useRef, useEffect } from 'react';
import HospitalScreenReal from './HospitalScreen';
import './index.css';

type Screen = 'home' | 'primeiros-socorros' | 'hospital' | 'camera' | 'info';

interface AnimalConfig {
  id: string; nome: string; apelido: string; risco: string;
  cor_risco: string; tamanho: string; regioes: string;
  descricao: string; foto: string;
}
interface AppConfig {
  versao: string;
  banner: { imagem: string };
  animais: AnimalConfig[];
}
function useConfig() {
  const [config, setConfig] = useState<AppConfig | null>(null);
  useEffect(() => {
    fetch('/config.json?v=' + Date.now())
      .then(r => r.json()).then(setConfig).catch(() => setConfig(null));
  }, []);
  return config;
}

const HOSPITALS = [
  {
    id: 1, primary: true,
    tag: 'Referência estadual',
    name: 'Hospital das Clínicas — FMUSP',
    addr: 'Av. Dr. Enéas Carvalho de Aguiar, 255 — Cerqueira César, SP',
    dist: '2,4 km',
    phone: '(11) 2661-0000',
    badges: ['✓ Soro disponível', '24h', 'Referência SP'],
    badgeTypes: ['badge-green', 'badge-blue', 'badge-amber'],
    maps: 'https://maps.google.com/?q=Hospital+das+Clinicas+FMUSP+Sao+Paulo'
  },
  {
    id: 2, primary: false,
    tag: 'Pronto-socorro',
    name: 'PS Central — Santana',
    addr: 'R. Voluntários da Pátria, 3882 — Santana, SP',
    dist: '5,1 km',
    phone: '(11) 2976-5000',
    badges: ['✓ Soro disponível', '24h'],
    badgeTypes: ['badge-green', 'badge-blue'],
    maps: 'https://maps.google.com/?q=Pronto+Socorro+Central+Santana+Sao+Paulo'
  },
  {
    id: 3, primary: false,
    tag: 'Hospital público',
    name: 'Hospital Geral de Vila Penteado',
    addr: 'Rua Catão, 593 — Vila Penteado, SP',
    dist: '7,3 km',
    phone: '(11) 3912-4300',
    badges: ['✓ Soro disponível', '24h'],
    badgeTypes: ['badge-green', 'badge-blue'],
    maps: 'https://maps.google.com/?q=Hospital+Geral+Vila+Penteado+Sao+Paulo'
  },
];

const SCORPION_STEPS = [
  { num: 1, urgent: false, title: 'Mantenha a calma', desc: 'Não aperte, corte, esfregue nem aplique torniquete. Não tente sugar o veneno — isso não funciona e piora.' },
  { num: 2, urgent: false, title: 'Lave com água e sabão', desc: 'Lavagem suave do local da picada com água corrente e sabão neutro. Não esfregue com força.' },
  { num: 3, urgent: false, title: 'Anote o horário da picada', desc: 'Informação essencial para o médico calcular a dose e urgência do antídoto.' },
  { num: 4, urgent: false, title: 'Observe os sintomas', desc: 'Dor local, formigamento, suor excessivo, vômito, agitação, tremores ou dificuldade de engolir = grave.' },
  { num: 5, urgent: true, title: '🏥 Vá ao hospital IMEDIATAMENTE', desc: 'Crianças, idosos e qualquer sintoma além da dor local = emergência máxima. O soro só é aplicado em hospital.' },
  { num: 6, urgent: false, title: 'Leve ou fotografe o animal', desc: 'Se conseguir fotografar com segurança, leve a foto ao hospital. Ajuda na escolha do soro correto.' },
];

const SCORPION_TYPES = [
  {
    name: 'Tityus serrulatus',
    apelido: 'Escorpião amarelo',
    risco: 'ALTO RISCO',
    cor: 'badge-red',
    cor2: '#C0392B',
    desc: 'O mais perigoso do Brasil. Responsável pela maioria dos óbitos. Coloração amarela uniforme, cauda fina e comprida. Predomina em SP, MG, GO, RJ. Altamente adaptado ao ambiente urbano.',
    onde: 'SP, MG, GO, RJ, MT',
    tamanho: '6–8 cm',
    foto: 'https://upload.wikimedia.org/wikipedia/commons/thumb/5/5d/Tityus_serrulatus.jpg/320px-Tityus_serrulatus.jpg',
    fotoCredito: 'Wikimedia Commons',
  },
  {
    name: 'Tityus bahiensis',
    apelido: 'Escorpião marrom',
    risco: 'MÉDIO RISCO',
    cor: 'badge-amber',
    cor2: '#E67E22',
    desc: 'Coloração marrom escura, pinças mais robustas que o T. serrulatus. Acidentes de gravidade moderada. Encontrado principalmente no Sul e Sudeste do Brasil.',
    onde: 'SP, PR, SC, RS, MG',
    tamanho: '5–7 cm',
    foto: 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/43/Tityus_bahiensis.jpg/320px-Tityus_bahiensis.jpg',
    fotoCredito: 'Wikimedia Commons',
  },
  {
    name: 'Tityus stigmurus',
    apelido: 'Escorpião do Nordeste',
    risco: 'MÉDIO RISCO',
    cor: 'badge-amber',
    cor2: '#E67E22',
    desc: 'Cor amarela com manchas escuras no dorso. Responsável pela maioria dos acidentes no Nordeste. Adapta-se bem a áreas urbanas e periurbanas.',
    onde: 'BA, PE, CE, PB, RN',
    tamanho: '5–7 cm',
    foto: 'https://upload.wikimedia.org/wikipedia/commons/thumb/b/b6/Tityus_stigmurus.jpg/320px-Tityus_stigmurus.jpg',
    fotoCredito: 'Wikimedia Commons',
  },
  {
    name: 'Rhopalurus sp.',
    apelido: 'Escorpião do semiárido',
    risco: 'BAIXO RISCO',
    cor: 'badge-green',
    cor2: '#1E8449',
    desc: 'Picada causa dor local intensa, mas raramente provoca sintomas sistêmicos em adultos saudáveis. Encontrado no semiárido nordestino. Menor risco de complicações graves.',
    onde: 'BA, PE, CE, PI, MA',
    tamanho: '4–6 cm',
    foto: 'https://upload.wikimedia.org/wikipedia/commons/thumb/8/8d/Rhopalurus_junceus.jpg/320px-Rhopalurus_junceus.jpg',
    fotoCredito: 'Wikimedia Commons',
  },
];

function BottomNav({ active, setScreen }: { active: string; setScreen: (s: Screen) => void }) {
  const tabs = [
    { id: 'home', icon: '🏠', label: 'Início' },
    { id: 'primeiros-socorros', icon: '🚨', label: 'Socorros' },
    { id: 'hospital', icon: '🏥', label: 'Hospitais' },
    { id: 'camera', icon: '📷', label: 'Fotografar' },
    { id: 'info', icon: '📚', label: 'Prevenção' },
  ];
  return (
    <div className="bottom-nav">
      {tabs.map(t => (
        <button key={t.id} className={`nav-btn ${active === t.id ? 'active' : ''}`}
          onClick={() => setScreen(t.id as Screen)}>
          <span className="nav-icon">{t.icon}</span>
          <span className="nav-label">{t.label}</span>
        </button>
      ))}
    </div>
  );
}

function HomeScreen({ setScreen, config }: { setScreen: (s: Screen) => void; config: any }) {
  const bannerImg = config?.banner?.imagem;
  return (
    <div className="screen">
      <div className="hero-header" style={bannerImg ? {
        backgroundImage: `linear-gradient(rgba(0,0,0,0.45), rgba(192,57,43,0.85)), url(${bannerImg})`,
        backgroundSize: 'cover', backgroundPosition: 'center'
      } : {}}>
        {!bannerImg && <span className="hero-emoji">🦂</span>}
        <h1>Socorro<br />Vivo</h1>
        <p>Guia rápido para acidentes com animais peçonhentos no Brasil</p>
      </div>
      <div className="scroll-content">
        <button className="emergency-btn" onClick={() => setScreen('primeiros-socorros')}>
          🚨 Fui picado — o que fazer agora?
        </button>

        {/* Botões SAMU e Bombeiros */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, margin: '0 1.25rem 1rem' }}>
          <a href="tel:192" style={{ textDecoration: 'none' }}>
            <div style={{
              background: '#E67E22', borderRadius: 'var(--radius)', padding: '14px 10px',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
              boxShadow: '0 4px 16px rgba(230,126,34,0.35)'
            }}>
              <span style={{ fontSize: 28 }}>🚑</span>
              <span style={{ color: '#fff', fontWeight: 700, fontSize: 22, fontFamily: 'var(--font-display)', lineHeight: 1 }}>192</span>
              <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12, fontWeight: 500 }}>SAMU</span>
            </div>
          </a>
          <a href="tel:193" style={{ textDecoration: 'none' }}>
            <div style={{
              background: '#C0392B', borderRadius: 'var(--radius)', padding: '14px 10px',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
              boxShadow: '0 4px 16px rgba(192,57,43,0.35)'
            }}>
              <span style={{ fontSize: 28 }}>🚒</span>
              <span style={{ color: '#fff', fontWeight: 700, fontSize: 22, fontFamily: 'var(--font-display)', lineHeight: 1 }}>193</span>
              <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12, fontWeight: 500 }}>Bombeiros</span>
            </div>
          </a>
        </div>

        <div className="alert-box danger">
          <span className="alert-icon">⚠️</span>
          <p><strong>Atenção:</strong> Escorpiões causam mais de 1,6 milhão de acidentes/ano no Brasil. Para crianças e idosos pode ser fatal. Procure hospital imediatamente.</p>
        </div>
        <div className="section-label">Animais — fase 1</div>
        <div className="chips-row">
          <span className="chip active">🦂 Escorpião</span>
          <span className="chip disabled">🕷 Aranha</span>
          <span className="chip disabled">🐍 Cobra</span>
          <span className="chip disabled">🐛 Lagarta</span>
        </div>
        <div className="section-label">Acesso rápido</div>
        <div className="card-grid">
          <div className="menu-card red-accent" onClick={() => setScreen('primeiros-socorros')}>
            <span className="mc-icon">💉</span>
            <span className="mc-label">Primeiros socorros</span>
            <span className="mc-sub">Passo a passo</span>
          </div>
          <div className="menu-card" onClick={() => setScreen('hospital')}>
            <span className="mc-icon">🏥</span>
            <span className="mc-label">Hospital com soro</span>
            <span className="mc-sub">Mais próximo</span>
          </div>
          <div className="menu-card" onClick={() => setScreen('camera')}>
            <span className="mc-icon">📷</span>
            <span className="mc-label">Fotografar animal</span>
            <span className="mc-sub">Mostrar ao médico</span>
          </div>
          <div className="menu-card" onClick={() => setScreen('info')}>
            <span className="mc-icon">📚</span>
            <span className="mc-label">Tipos e prevenção</span>
            <span className="mc-sub">Habitat</span>
          </div>
        </div>
        <div className="section-label">Situação no Brasil</div>
        <div className="stat-strip">
          <div className="stat-item">
            <div className="stat-num">1,6M</div>
            <div className="stat-lbl">acidentes/ano</div>
          </div>
          <div className="stat-item">
            <div className="stat-num">+80%</div>
            <div className="stat-lbl">crescimento escorpiões</div>
          </div>
          <div className="stat-item">
            <div className="stat-num">90%</div>
            <div className="stat-lbl">óbitos crianças/idosos</div>
          </div>
        </div>
        <div className="section-label">Emergências</div>
        <div className="info-card">
          <h4>📞 Números importantes</h4>
          <p>
            <strong>SAMU:</strong> 192 &nbsp;·&nbsp; <strong>Bombeiros:</strong> 193<br />
            <strong>CVS:</strong> 0800 722 6001<br />
            <strong>CEATOX SP:</strong> (11) 5012-5555
          </p>
        </div>
        <div className="pb-safe" />
      </div>
      <BottomNav active="home" setScreen={setScreen} />
    </div>
  );
}

function PrimeirosSocorrosScreen({ setScreen }: { setScreen: (s: Screen) => void }) {
  return (
    <div className="screen">
      <div className="sub-header">
        <button className="back-btn" onClick={() => setScreen('home')}>←</button>
        <div className="sub-header-text">
          <h2>🦂 Picada de escorpião</h2>
          <p>Siga os passos na ordem</p>
        </div>
      </div>
      <div className="scroll-content">
        <div style={{ height: '1rem' }} />
        <div className="steps-list">
          {SCORPION_STEPS.map(step => (
            <div key={step.num} className={`step-card ${step.urgent ? 'urgent' : ''}`}>
              <div className="step-num">{step.num}</div>
              <div className="step-content">
                <h4>{step.title}</h4>
                <p>{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="alert-box warning">
          <span className="alert-icon">⛔</span>
          <p><strong>NÃO FAÇA:</strong> não corte, não esfregue, não use torniquete, não aplique gelo, não tente sugar o veneno, não dê remédio caseiro.</p>
        </div>
        <button className="emergency-btn" onClick={() => setScreen('hospital')}>
          🏥 Ver hospital com soro próximo
        </button>
        <div className="section-label">Sintomas de gravidade — vá ao hospital SE houver:</div>
        <div className="info-card">
          <h4>🔴 Sinais de alerta</h4>
          <ul>
            <li>Vômito intenso e repetido</li>
            <li>Suor frio excessivo</li>
            <li>Agitação extrema ou sonolência</li>
            <li>Tremores ou convulsão</li>
            <li>Dificuldade para respirar ou engolir</li>
            <li>Pressão baixa ou desmaio</li>
            <li>Criança ou idoso picado (sempre urgência)</li>
          </ul>
        </div>
        <div className="pb-safe" />
      </div>
      <BottomNav active="primeiros-socorros" setScreen={setScreen} />
    </div>
  );
}

function HospitalScreen({ setScreen }: { setScreen: (s: Screen) => void }) {
  const [search, setSearch] = useState('');
  return (
    <div className="screen">
      <div className="sub-header">
        <button className="back-btn" onClick={() => setScreen('home')}>←</button>
        <div className="sub-header-text">
          <h2>🏥 Hospital com soro</h2>
          <p>Antiescorpiônico disponível</p>
        </div>
      </div>
      <div className="scroll-content">
        <div className="search-bar">
          <input type="text" placeholder="Digite CEP ou cidade para buscar..."
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="alert-box info">
          <span className="alert-icon">📍</span>
          <p>Mostrando hospitais próximos a <strong>São Paulo, SP</strong>. Confirme disponibilidade do soro por telefone antes de sair.</p>
        </div>
        {HOSPITALS.map(h => (
          <div key={h.id} className={`hospital-card ${h.primary ? 'primary' : ''}`}>
            <div className="hosp-tag">{h.primary ? '⭐ ' : ''}{h.tag} · {h.dist}</div>
            <div className="hosp-name">{h.name}</div>
            <div className="hosp-addr">{h.addr}</div>
            <div className="hosp-addr" style={{ marginTop: 4 }}>📞 {h.phone}</div>
            <div className="hosp-badges">
              {h.badges.map((b, i) => (
                <span key={i} className={`badge ${h.badgeTypes[i]}`}>{b}</span>
              ))}
            </div>
            <button className="route-btn" onClick={() => window.open(h.maps, '_blank')}>
              📍 Abrir rota no Maps
            </button>
            <button className="route-btn secondary" style={{ marginTop: 6 }}
              onClick={() => window.open(`tel:${h.phone.replace(/\D/g, '')}`)}>
              📞 Ligar agora
            </button>
          </div>
        ))}
        <div className="info-card" style={{ marginTop: 4 }}>
          <h4>ℹ️ Sobre os dados</h4>
          <p>Hospitais credenciados pela Secretaria de Saúde-SP. Dados via CNES/DataSUS. Nem todo PS tem soro antiescorpiônico — sempre confirme por telefone antes de ir.</p>
        </div>
        <div className="pb-safe" />
      </div>
      <BottomNav active="hospital" setScreen={setScreen} />
    </div>
  );
}

function CameraScreen({ setScreen, config }: { setScreen: (s: Screen) => void; config: any }) {
  // Usa animais do config.json se disponível, senão usa os dados embutidos
  const COR_MAP: Record<string, string> = { red: 'badge-red', amber: 'badge-amber', green: 'badge-green' };
  const animais = config?.animais ?? SCORPION_TYPES.map((s: any) => ({
    nome: s.name, apelido: s.apelido, risco: s.risco,
    cor_risco: s.cor === 'badge-red' ? 'red' : s.cor === 'badge-amber' ? 'amber' : 'green',
    tamanho: s.tamanho, regioes: s.onde, descricao: s.desc, foto: s.foto
  }));
  const [image, setImage] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setImage(reader.result as string);
    reader.readAsDataURL(file);
  };

  return (
    <div className="screen">
      <div className="sub-header">
        <button className="back-btn" onClick={() => setScreen('home')}>←</button>
        <div className="sub-header-text">
          <h2>📷 Fotografar animal</h2>
          <p>Compare com as espécies abaixo</p>
        </div>
      </div>
      <div className="scroll-content">
        <input ref={fileRef} type="file" accept="image/*" capture="environment"
          className="file-input" onChange={handleFile} />

        {!image ? (
          <div className="cam-zone" onClick={() => fileRef.current?.click()}>
            <span className="cam-icon">📷</span>
            <p>Tocar para fotografar</p>
            <small>Ou selecionar da galeria</small>
          </div>
        ) : (
          <div style={{ padding: '1rem 1.25rem 0.75rem' }}>
            <img src={image} alt="Animal fotografado" className="img-preview" />
            <button className="route-btn secondary" onClick={() => { setImage(null); if (fileRef.current) fileRef.current.value = ''; }}>
              🔄 Tirar nova foto
            </button>
          </div>
        )}

        <div className="alert-box info">
          <span className="alert-icon">💡</span>
          <p>Fotografe com segurança, à distância. <strong>Não tente capturar.</strong> Mostre a foto ao médico — ajuda na escolha do soro correto.</p>
        </div>


        <div className="section-label">Espécies mais comuns — compare</div>
        {animais.map((s: any, i: number) => (
          <div key={i} className="info-card" style={{ padding: 0, overflow: "hidden" }}>
            <div style={{ position: "relative" }}>
              {s.foto ? (
                <img src={s.foto} alt={s.nome}
                  style={{ width: "100%", height: 180, objectFit: "cover", display: "block" }}
                  onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                />
              ) : (
                <div style={{ width: "100%", height: 80, background: "var(--gray-100)",
                  display: "flex", alignItems: "center", justifyContent: "center", fontSize: 40 }}>🦂</div>
              )}
              <span className={`badge badge-${s.cor_risco === "red" ? "red" : s.cor_risco === "amber" ? "amber" : "green"}`}
                style={{ position: "absolute", top: 10, right: 10, boxShadow: "0 2px 8px rgba(0,0,0,0.3)" }}>
                {s.risco}
              </span>
            </div>
            <div style={{ padding: "12px 14px" }}>
              <h4 style={{ marginBottom: 3 }}>{s.nome}</h4>
              <p style={{ fontSize: 12, color: "var(--gray-400)", marginBottom: 6 }}>
                <em>{s.apelido}</em> · {s.tamanho} · {s.regioes}
              </p>
              <p style={{ fontSize: 13, color: "var(--gray-600)", lineHeight: 1.5 }}>{s.descricao}</p>
            </div>
          </div>
        ))}

        <div className="alert-box warning" style={{ marginTop: 8 }}>
          <span className="alert-icon">⚠️</span>
          <p>A identificação visual é apenas orientativa. O diagnóstico e escolha do soro é sempre responsabilidade do médico.</p>
        </div>
        <div className="pb-safe" />
      </div>
      <BottomNav active="camera" setScreen={setScreen} />
    </div>
  );
}

function InfoScreen({ setScreen }: { setScreen: (s: Screen) => void }) {
  return (
    <div className="screen">
      <div className="sub-header">
        <button className="back-btn" onClick={() => setScreen('home')}>←</button>
        <div className="sub-header-text">
          <h2>📚 Habitat e prevenção</h2>
          <p>Como evitar acidentes em casa</p>
        </div>
      </div>
      <div className="scroll-content">
        <div style={{ height: '1rem' }} />
        <div className="alert-box danger">
          <span className="alert-icon">📊</span>
          <p><strong>Cenário atual:</strong> O escorpião é o animal que mais cresce em incidência no Brasil. Urbanização, entulho e proliferação de baratas (alimento favorito) impulsionam a expansão.</p>
        </div>
        <div className="section-label">Onde escorpiões se instalam</div>
        <div className="info-card">
          <h4>🔍 Locais favoritos</h4>
          <ul>
            <li>Embaixo de pedras, telhas, tijolos e entulho</li>
            <li>Fendas em paredes, rodapés e pisos</li>
            <li>Dentro de sapatos, roupas e toalhas no chão</li>
            <li>Caixas de papelão guardadas</li>
            <li>Vasos de plantas e jardins</li>
            <li>Esgoto e bueiros — entram por ralos</li>
          </ul>
        </div>
        <div className="section-label">Prevenção dentro de casa</div>
        <div className="info-card">
          <h4>🏠 Hábitos essenciais</h4>
          <ul>
            <li>Sempre sacuda sapatos e roupas antes de usar</li>
            <li>Não deixe roupas ou toalhas no chão</li>
            <li>Cuidado ao mexer em caixas guardadas há tempo</li>
            <li>Sacuda lençóis antes de dormir em áreas de risco</li>
          </ul>
        </div>
        <div className="info-card">
          <h4>🚫 Como barrar a entrada</h4>
          <ul>
            <li><strong>Vede frestas</strong> em paredes, janelas e rodapés com silicone</li>
            <li><strong>Telas nos ralos</strong> de banheiro, pia e tanque</li>
            <li><strong>Elimine entulho</strong> do quintal regularmente</li>
            <li><strong>Barreira de grude</strong> nos pés de camas e berços de bebê</li>
            <li><strong>Controle baratas</strong> — principal alimento do escorpião</li>
            <li><strong>Evite palmeiras</strong> próximas à casa — atraem escorpiões</li>
          </ul>
        </div>
        <div className="info-card">
          <h4>👶 Crianças e idosos — alto risco</h4>
          <p>Verifique o quarto diariamente. Use barreira de grude em berços e camas. Não deixe brinquedos no chão à noite. Qualquer picada nesses grupos = emergência imediata, mesmo sem sintomas graves.</p>
        </div>
        <div className="info-card">
          <h4>⚠️ Encontrou um escorpião vivo?</h4>
          <p>Use luvas grossas + pinça, ou coloque um copo sobre o animal e deslize um papelão por baixo. Leve à Vigilância de Zoonoses do município — eles identificam a espécie e orientam o controle no imóvel.</p>
        </div>
        <button className="emergency-btn" style={{ margin: '1rem 1.25rem' }} onClick={() => setScreen('primeiros-socorros')}>
          💉 Ver protocolo de primeiros socorros
        </button>
        <div className="pb-safe" />
      </div>
      <BottomNav active="info" setScreen={setScreen} />
    </div>
  );
}

export default function App() {
  const [screen, setScreen] = useState<Screen>('home');
  const config = useConfig();
  return (
    <div className="app">
      {screen === 'home' && <HomeScreen setScreen={setScreen} config={config} />}
      {screen === 'primeiros-socorros' && <PrimeirosSocorrosScreen setScreen={setScreen} />}
      {screen === 'hospital' && <HospitalScreenReal setScreen={setScreen} />}
      {screen === 'camera' && <CameraScreen setScreen={setScreen} config={config} />}
      {screen === 'info' && <InfoScreen setScreen={setScreen} />}
    </div>
  );
}
